import { useEffect } from "react";
import { SectionTitle } from "@/components/ui";
import { useT } from "@/lib/i18n";

const GROUPS: { title: string; rows: [string, string][] }[] = [];

export function ShortcutsDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const sc = (k: string) => t.shortcuts[k] ?? k;
  const g = (k: string) => t.shortcutGroups[k] ?? k;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && (e.stopPropagation(), onClose());
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [onClose]);
  const groups: typeof GROUPS = [
    {
      title: g("canvas"),
      rows: [
        [sc("pan"), "Espacio + arrastrar / rueda"],
        [sc("zoom"), "Ctrl + rueda / Ctrl + + / Ctrl + −"],
        [sc("zoomFit"), "Z"],
        [sc("resetZoom"), "Ctrl + 0"],
        [sc("newNote"), sc("dblClick")],
        [sc("search"), "Ctrl + /"],
        [sc("present"), "P"],
        [sc("help"), "?"],
      ],
    },
    {
      title: g("cards"),
      rows: [
        [sc("selectAll"), "Ctrl + A"],
        [sc("edit"), "Enter / " + sc("dblClick")],
        [sc("duplicate"), "Ctrl + D"],
        [sc("connect"), "Ctrl + K"],
        [sc("delete"), "Supr / Retroceso"],
        [sc("nudge"), "← ↑ → ↓ (Shift: ×5)"],
        [sc("front"), "Ctrl + Shift + ↑"],
        [sc("back"), "Ctrl + Shift + ↓"],
        [sc("undo"), "Ctrl + Z / Ctrl + Shift + Z"],
        [sc("multi"), "Shift + clic / arrastrar en el lienzo"],
      ],
    },
    {
      title: g("text"),
      rows: [
        [sc("bold"), "Ctrl + B"],
        [sc("italic"), "Ctrl + I"],
        [sc("link"), "Ctrl + K " + sc("inText")],
        [sc("finish"), "Esc"],
      ],
    },
  ];
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/70 p-4 pt-16"
      onPointerDown={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-2xl rounded-md border border-border bg-panel p-6 shadow-panel">
        <SectionTitle>{t.shortcutsTitle}</SectionTitle>
        <div className="grid gap-6 sm:grid-cols-2">
          {groups.map((g) => (
            <div key={g.title}>
              <div className="mb-2 text-xs uppercase tracking-wide text-dim">{g.title}</div>
              <table className="w-full text-sm">
                <tbody>
                  {g.rows.map(([label, keys]) => (
                    <tr key={label} className="border-t border-border/60">
                      <td className="py-1 pr-3 text-muted">{label}</td>
                      <td className="py-1 text-right font-mono text-xs text-text">{keys}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between">
          <a href="/guia" className="text-sm text-muted hover:text-text">
            {t.guide} →
          </a>
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-text">
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
}
